// src/routes/domains/uk/uk.routes.tsx

import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { createProtectedRoute } from '../../core/createProtectedRoute';
import type { AppRoute } from '../../core/types';

// ------------------------------
// Eagerly loaded (public) marketing hub
// ------------------------------
import UKImmigrationHub from '@/pages/marketing/UKImmigrationHub';

// ------------------------------
// Lazy‑loaded UK components (absolute imports)
// ------------------------------

// Core simulator & layout
const UKPathwayDreamsEngine = lazy(() => import('@/pages/UKPathwayDreamsEngine'));

// Standalone tools & calculators
const UKGlobalSalaryCalculator = lazy(() => import('@/pages/UKGlobalSalaryCalculator'));
const UKEnglishTestSimulator = lazy(() => import('@/pages/UKEnglishTestSimulator'));
const UKPhotoValidator = lazy(() => import('@/pages/UKPhotoValidator'));
const UKRiskAssessment = lazy(() => import('@/pages/UKRiskAssessment'));
const UKSuccessPredictionEngine = lazy(() => import('@/pages/UKSuccessPredictionEngine'));
const UKTimelineForecaster = lazy(() => import('@/pages/UKTimelineForecaster'));
const UKPathwayAnalyticsDashboard = lazy(() => import('@/pages/UKPathwayAnalyticsDashboard'));
const UKToolsEcosystem = lazy(() => import('@/pages/uk-premium/UKToolsEcosystem'));
const UKVisaFormSimulator = lazy(() => import('@/pages/UKVisaFormSimulator'));
const UKVIBiometricAppointmentScheduler = lazy(() => import('@/pages/UKVIBiometricAppointmentScheduler'));
const ILRPathwayPlanner = lazy(() => import('@/pages/ilr-pathway-planner'));
const EUSettlementTools = lazy(() => import('@/pages/EUSettlementTools'));
const IHSSurchargeCalculator = lazy(() => import('@/pages/IHSSurchargeCalculator'));
const CoSValidator = lazy(() => import('@/components/tools/CoSValidator'));
const CosTracker = lazy(() => import('@/pages/CosTracker'));
const UKExperienceValidator = lazy(() => import('@/components/tools/UKExperienceValidator'));
const ShortageOccupationChecker = lazy(() => import('@/components/tools/ShortageOccupationChecker'));
const UKStudentLoanMatcher = lazy(() => import('@/pages/UKStudentLoanMatcher'));
const ProfessionalHealthcareRoutes = lazy(() => import('@/pages/ProfessionalHealthcareRoutes'));
const YouthStudentTools = lazy(() => import('@/pages/YouthStudentTools'));
const FinancialMaintenance = lazy(() => import('@/pages/FinancialMaintenanceTools'));
const VisaExpiryTools = lazy(() => import('@/pages/VisaExpiryExtensionTools'));
const AccommodationHousing = lazy(() => import('@/pages/AccommodationHousing'));
const UKOpportunitiesHub = lazy(() => import('@/pages/uk-opportunities'));
const OpportunitiesHub = lazy(() => import('@/modules/uk/OpportunitiesHub'));
const SponsorLicenceCheck = lazy(() => import('@/pages/SponsorLicenceCheck'));
const SponsorDirectory = lazy(() => import('@/pages/SponsorDirectory'));
const SponsorDetail = lazy(() => import('@/pages/SponsorDetail'));

// ------------------------------
// UK Routes Definition
// ------------------------------
export const ukRoutes: AppRoute[] = [
  // 1. Public marketing hub (no authentication)
  {
    id: 'uk.marketing_hub',
    domain: 'uk',
    path: '/uk-immigration-hub',
    element: createProtectedRoute(<UKImmigrationHub />, {
      protected: false,
      meta: {
        title: 'UK Immigration Hub | Free Guides & Tools',
        region: 'UK',
        intent: 'guide',
        aiTags: ['uk', 'immigration', 'hub', 'overview'],
      },
    }),
  },

  // 2. UK Visa Simulator ecosystem (Dashboard, application sub-pages, UKVI status,
  // interview simulator) — retired in favor of /journey-compass, the UPRM-wired
  // Migration Journey Timeline. Files archived to archive/uk-visa-simulator/ for
  // future reuse rather than deleted; every path that used to live under this tree
  // now redirects to its replacement.
  {
    id: 'uk.simulator_retired',
    domain: 'uk',
    path: '/uk-visa-simulator/*',
    element: <Navigate to="/journey-compass" replace />,
  },
  {
    id: 'uk.vi_status',
    domain: 'uk',
    path: '/ukvi-status/:id',
    element: <Navigate to="/journey-compass" replace />,
  },
  {
    id: 'uk.interview_simulator',
    domain: 'uk',
    path: '/uk-interview-simulator',
    element: <Navigate to="/interview-simulator" replace />,
  },
  {
    id: 'uk.global_salary',
    domain: 'uk',
    path: '/uk-global-salary-calculator',
    element: createProtectedRoute(<UKGlobalSalaryCalculator />, {
      tier: 'premium',
      meta: { title: 'UK Global Salary Calculator', region: 'UK', intent: 'calculator', aiTags: ['salary', 'global'] },
    }),
  },
  {
    id: 'uk.english_test',
    domain: 'uk',
    path: '/uk-english-test-simulator',
    element: createProtectedRoute(<UKEnglishTestSimulator />, {
      tier: 'premium',
      meta: { title: 'UK English Test Simulator', region: 'UK', intent: 'simulator', aiTags: ['ielts', 'english'] },
    }),
  },
  {
    id: 'uk.photo_validator',
    domain: 'uk',
    path: '/uk-photo-validator',
    element: createProtectedRoute(<UKPhotoValidator />, {
      tier: 'premium',
      meta: { title: 'UK Visa Photo Validator', region: 'UK', intent: 'validator', aiTags: ['photo', 'specifications'] },
    }),
  },
  {
    id: 'uk.risk_assessment',
    domain: 'uk',
    path: '/uk-risk-assessment',
    element: createProtectedRoute(<UKRiskAssessment />, {
      tier: 'premium',
      meta: { title: 'UK Visa Risk Assessment', region: 'UK', intent: 'assessment', aiTags: ['risk', 'refusal'], toolId: 'uk-risk-assessment', category: 'assessments' },
    }),
  },
  {
    id: 'uk.success_prediction',
    domain: 'uk',
    path: '/uk-success-prediction',
    element: createProtectedRoute(<UKSuccessPredictionEngine />, {
      tier: 'premium',
      meta: { title: 'UK Success Prediction', region: 'UK', intent: 'prediction', aiTags: ['success', 'prediction'], toolId: 'uk-success-predictor', category: 'assessments' },
    }),
  },
  {
    id: 'uk.timeline_forecaster',
    domain: 'uk',
    path: '/uk-timeline-forecaster',
    element: createProtectedRoute(<UKTimelineForecaster />, {
      tier: 'premium',
      meta: { title: 'UK Timeline Forecaster', region: 'UK', intent: 'forecaster', aiTags: ['timeline', 'processing'], toolId: 'uk-timeline-forecaster', category: 'assessments' },
    }),
  },
  {
    id: 'uk.pathway_analytics',
    domain: 'uk',
    path: '/uk-pathway-analytics',
    element: createProtectedRoute(<UKPathwayAnalyticsDashboard />, {
      tier: 'premium',
      meta: { title: 'UK Pathway Analytics', region: 'UK', intent: 'dashboard', aiTags: ['analytics', 'pathway'], toolId: 'uk-pathway-analytics', category: 'planning' },
    }),
  },
  {
    id: 'uk.tools_ecosystem',
    domain: 'uk',
    path: '/uk-tools-ecosystem',
    element: createProtectedRoute(<UKToolsEcosystem />, {
      tier: 'premium',
      meta: { title: 'UK Tools Ecosystem', region: 'UK', intent: 'dashboard', aiTags: ['ecosystem', 'tools'] },
    }),
  },
  {
    id: 'uk.integration_hub',
    domain: 'uk',
    path: '/uk-integration-hub',
    // Canonical page is /uk-integration (core)
    element: <Navigate to="/uk-integration" replace />,
  },
  {
    id: 'uk.visa_form_simulator',
    domain: 'uk',
    path: '/uk-visa-form-simulator',
    element: createProtectedRoute(<UKVisaFormSimulator />, {
      tier: 'premium',
      meta: { title: 'UK Visa Form Simulator', region: 'UK', intent: 'form', aiTags: ['application', 'form'] },
    }),
  },
  {
    id: 'uk.biometric_scheduler',
    domain: 'uk',
    path: '/uk-biometric-appointment',
    element: createProtectedRoute(<UKVIBiometricAppointmentScheduler />, {
      tier: 'premium',
      meta: { title: 'UKVI Biometric Scheduler', region: 'UK', intent: 'booking', aiTags: ['biometric', 'appointment'] },
    }),
  },
  {
    id: 'uk.pilot_flight_deck',
    domain: 'uk',
    path: '/uk-pilot-flight-deck',
    // Superseded by FlightOS — archived UKPilotFlightDeck
    element: <Navigate to="/flightos" replace />,
  },
  {
    id: 'uk.ilr_pathway',
    domain: 'uk',
    path: '/ilr-pathway-planner',
    element: createProtectedRoute(<ILRPathwayPlanner />, {
      tier: 'premium',
      meta: { title: 'ILR Pathway Planner', region: 'UK', intent: 'planner', aiTags: ['ilr', 'indefinite leave'], toolId: 'uk-ilr-pathway-planner', category: 'planning' },
    }),
  },
  {
    id: 'uk.eu_settlement',
    domain: 'uk',
    path: '/eu-settlement-tools',
    element: createProtectedRoute(<EUSettlementTools />, {
      tier: 'premium',
      meta: { title: 'EU Settlement Scheme Tools', region: 'UK', intent: 'tool', aiTags: ['eu', 'settlement'] },
    }),
  },
  {
    id: 'uk.ihs_calculator',
    domain: 'uk',
    path: '/ihs-surcharge-calculator',
    element: createProtectedRoute(<IHSSurchargeCalculator />, {
      tier: 'premium',
      meta: { title: 'IHS Surcharge Calculator', region: 'UK', intent: 'calculator', aiTags: ['ihs', 'health surcharge'] },
    }),
  },
  {
    id: 'uk.cos_validator',
    domain: 'uk',
    path: '/cos-validator',
    element: createProtectedRoute(<CoSValidator />, {
      tier: 'premium',
      meta: { title: 'Certificate of Sponsorship (CoS) Validator', region: 'UK', intent: 'validator', aiTags: ['cos', 'sponsorship'] },
    }),
  },
  {
    id: 'uk.cos_tracker',
    domain: 'uk',
    path: '/cos-tracker',
    element: createProtectedRoute(<CosTracker />, {
      tier: 'premium',
      meta: { title: 'CoS Tracker', region: 'UK', intent: 'tool', aiTags: ['cos', 'tracking'], toolId: 'uk-cos-tracker', category: 'financial' },
    }),
  },
  {
    id: 'uk.experience_validator',
    domain: 'uk',
    path: '/uk-experience-validator',
    element: createProtectedRoute(<UKExperienceValidator />, {
      tier: 'premium',
      meta: { title: 'UK Work Experience Validator', region: 'UK', intent: 'validator', aiTags: ['experience', 'work'] },
    }),
  },
  {
    id: 'uk.english_assessor',
    domain: 'uk',
    path: '/english-language-assessor',
    // EnglishLanguageAssessor archived — same job as UKEnglishTestSimulator, which stays.
    element: <Navigate to="/uk-english-test-simulator" replace />,
  },
  {
    id: 'uk.shortage_checker',
    domain: 'uk',
    path: '/shortage-occupation-checker',
    element: createProtectedRoute(<ShortageOccupationChecker />, {
      tier: 'premium',
      meta: { title: 'Shortage Occupation Checker', region: 'UK', intent: 'checker', aiTags: ['shortage', 'occupation'] },
    }),
  },
  {
    id: 'uk.global_salary_dedicated',
    domain: 'uk',
    path: '/global-salary-calculator',
    // GlobalSalaryCalculator archived — same job as UKGlobalSalaryCalculator, which stays.
    element: <Navigate to="/uk-global-salary-calculator" replace />,
  },
  {
    id: 'uk.student_loan',
    domain: 'uk',
    path: '/uk-student-loan-matcher',
    element: createProtectedRoute(<UKStudentLoanMatcher />, {
      tier: 'premium',
      meta: { title: 'UK Student Loan Matcher', region: 'UK', intent: 'tool', aiTags: ['student', 'loan'] },
    }),
  },
  {
    id: 'uk.healthcare_routes',
    domain: 'uk',
    path: '/uk-healthcare-routes',
    element: createProtectedRoute(<ProfessionalHealthcareRoutes />, {
      tier: 'premium',
      meta: { title: 'UK Healthcare Routes', region: 'UK', intent: 'guide', aiTags: ['healthcare', 'professional'] },
    }),
  },
  {
    id: 'uk.youth_student_tools',
    domain: 'uk',
    path: '/uk-youth-student-tools',
    element: createProtectedRoute(<YouthStudentTools />, {
      tier: 'premium',
      meta: { title: 'UK Youth & Student Tools', region: 'UK', intent: 'tool', aiTags: ['youth', 'student'] },
    }),
  },
  {
    id: 'uk.financial_maintenance',
    domain: 'uk',
    path: '/uk-financial-maintenance',
    element: createProtectedRoute(<FinancialMaintenance />, {
      tier: 'premium',
      meta: { title: 'UK Financial Maintenance Tools', region: 'UK', intent: 'tool', aiTags: ['financial', 'maintenance'] },
    }),
  },
  {
    id: 'uk.visa_expiry',
    domain: 'uk',
    path: '/uk-visa-expiry-tools',
    element: createProtectedRoute(<VisaExpiryTools />, {
      tier: 'premium',
      meta: { title: 'UK Visa Expiry & Extension Tools', region: 'UK', intent: 'tool', aiTags: ['expiry', 'extension'] },
    }),
  },
  {
    id: 'uk.accommodation_housing',
    domain: 'uk',
    path: '/uk-accommodation-housing',
    element: createProtectedRoute(<AccommodationHousing />, {
      tier: 'premium',
      meta: { title: 'UK Accommodation & Housing', region: 'UK', intent: 'tool', aiTags: ['housing', 'accommodation'] },
    }),
  },
  {
    id: 'uk.opportunities_hub',
    domain: 'uk',
    path: '/uk-opportunities',
    element: <Navigate to="/opportunity" replace />,
  },
  {
    id: 'uk.opportunities_module',
    domain: 'uk',
    path: '/uk-opportunities-module',
    element: <Navigate to="/opportunity/study" replace />,
  },
  {
    id: 'uk.pathway_dreams',
    domain: 'uk',
    path: '/uk-pathway-dreams',
    element: <Navigate to="/opportunity/work" replace />,
  },
  {
    id: 'uk.visa_simulator_root',
    domain: 'uk',
    path: '/uk-visa-simulator-root',
    element: <Navigate to="/journey-compass" replace />,
  },

  // UK COS V3 Phase 2 -- real Sponsor Register (public.uk_sponsor_register,
  // 142,456 real GOV.UK rows). No fabricated data, no invented scores.
  {
    id: 'uk.sponsor_licence_check',
    domain: 'uk',
    path: '/sponsor-licence-check',
    element: createProtectedRoute(<SponsorLicenceCheck />, {
      tier: 'premium',
      meta: { title: 'Sponsor Licence Check', region: 'UK', intent: 'assessment', aiTags: ['sponsor', 'licence', 'search'], toolId: 'uk-sponsor-licence-check', category: 'compliance' },
    }),
  },
  {
    id: 'uk.sponsor_directory',
    domain: 'uk',
    path: '/sponsor-directory',
    element: <Navigate to="/opportunity/sponsors" replace />,
  },
  {
    id: 'uk.sponsor_detail',
    domain: 'uk',
    path: '/sponsor/:organisationName',
    element: createProtectedRoute(<SponsorDetail />, {
      tier: 'premium',
      meta: { title: 'Sponsor Detail', region: 'UK', intent: 'document', aiTags: ['sponsor', 'detail'], toolId: 'uk-sponsor-detail', category: 'compliance' },
    }),
  },
];